import type { GameWalletItem } from '@/features/finance/services/paymentService';
import type { DeviceClass } from '@/config/uiScale';
import type { GameProvider } from '@/features/games/data/gameProviders';
import { isProviderDepositLocked } from '@/features/games/services/gameProviderHealthService';

/**
 * Play (spec docs/specs/2026-10-08-kiosk-web-split-design.md §4): when a game can be played and
 * how its site opens outside the app. Whether the surface allows Play at all is the caller's
 * question (`useSurface().can('play')`, see usePlayGame).
 */

/** The game link when it is an absolute http(s) URL (normalised), else null. Only these ever open. */
export function safeGameUrl(raw: string | null | undefined): string | null {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

type GameAvailability = Pick<GameProvider, 'status' | 'depositsOpen'>;

function unavailable(game: Partial<GameAvailability> | null | undefined): boolean {
  if (!game) return false;
  return game.status === 'inactive' || game.status === 'maintenance' || isProviderDepositLocked(game);
}

/**
 * The player can play this game: a linked account (game username), an http(s) game link, and the
 * game is neither Coming soon nor under maintenance. The health snapshot can sit on the wallet row
 * (My games merges it) or on the catalog game (Play, the game sheet): either one closes Play.
 */
export function canPlayGame(
  wallet: (GameWalletItem & { depositsOpen?: boolean }) | null | undefined,
  catalogGame?: Partial<GameAvailability> | null
): boolean {
  const game = wallet?.game;
  if (!game) return false;
  if (!wallet.account?.gameUsername) return false;
  if (!safeGameUrl(game.gameUrl)) return false;
  if (unavailable(game) || isProviderDepositLocked(wallet)) return false;
  return !unavailable(catalogGame);
}

/** One named window for every game on desktop: Play again focuses and reuses it (v1 behaviour). */
export const GAME_WINDOW_NAME = 'kioskExternalGame';

/** The new page cannot reach back into the app (reverse tabnabbing) once it has an opener. */
function detach(win: Window | null): Window | null {
  if (!win) return null;
  try {
    win.opener = null;
  } catch {
    /* cross-origin already: nothing to cut */
  }
  return win;
}

/**
 * A new browser tab. `noopener` is not passed as a feature because then `window.open` always
 * answers null and a blocked popup could not be told apart; the opener is cut right after instead.
 * Null when blocked or when the link is not http(s).
 */
export function openGameInNewTab(rawUrl: string): Window | null {
  const url = safeGameUrl(rawUrl);
  if (!url) return null;
  return detach(window.open(url, '_blank'));
}

/** Desktop: a window the size of the screen minus a small margin, reused by name (v1 gameLaunchUtils). */
function openGameNearFullscreen(url: string): Window | null {
  const margin = 12;
  /** availLeft / availTop: the usable area's corner on multi-screen setups (not in TS's Screen type). */
  const scr = window.screen as (Screen & { availLeft?: number; availTop?: number }) | undefined;
  const screenLeft = typeof scr?.availLeft === 'number' ? scr.availLeft : 0;
  const screenTop = typeof scr?.availTop === 'number' ? scr.availTop : 0;
  const width = Math.max(480, (scr?.availWidth || window.innerWidth) - margin * 2);
  const height = Math.max(360, (scr?.availHeight || window.innerHeight) - margin * 2);
  const features = [
    'popup=yes',
    `width=${width}`,
    `height=${height}`,
    `left=${screenLeft + margin}`,
    `top=${screenTop + margin}`,
    'menubar=no',
    'toolbar=no',
    'location=yes',
    'status=no',
    'scrollbars=yes',
    'resizable=yes',
  ].join(',');
  const win = detach(window.open(url, GAME_WINDOW_NAME, features));
  try {
    win?.focus();
  } catch {
    /* focus is best effort */
  }
  return win;
}

export interface GameOpenContext {
  /** DeviceModeProvider's device class (null when unknown). */
  device: DeviceClass | null;
  /** Running as the installed app (display-mode: standalone). */
  installedApp: boolean;
}

/**
 * Opens a game site outside the app: on a desktop browser the near-full-screen named window; on
 * phones, tablets, the installed app (and an unknown device) a new tab. Null when the browser
 * blocked it or the link is not http(s): the caller then offers the link to tap.
 */
export function openGameExternally(rawUrl: string, { device, installedApp }: GameOpenContext): Window | null {
  const url = safeGameUrl(rawUrl);
  if (!url) return null;
  if (device === 'desktop' && !installedApp) return openGameNearFullscreen(url);
  return detach(window.open(url, '_blank'));
}

const DEVICE_CLASSES: readonly DeviceClass[] = ['phone', 'tablet', 'kiosk', 'desktop'];

/**
 * The device class DeviceModeProvider publishes on `<body data-device>` (applyUiScale), read when
 * the player taps "Open game". Read from the DOM rather than useUiScale() because the game sheet
 * renders in many places (and tests) without a DeviceModeProvider, where useUiScale() throws.
 */
export function currentDeviceClass(): DeviceClass | null {
  if (typeof document === 'undefined') return null;
  const value = document.body?.dataset.device as DeviceClass | undefined;
  return value && DEVICE_CLASSES.includes(value) ? value : null;
}
